import type { DetectedSignatureField } from 'src/components/viewer/signature-field-navigation';

/**
 * Points of padding around a signer's printed name. The signature sits beside
 * the name: above it on most signature lines, to its left on DocuSign blocks,
 * and below it where the name heads the block, so the outline is widened every
 * way to take it in.
 */
const NAME_PADDING = { above: 42, below: 42, sides: 48 } as const;

/**
 * Furthest a detected signature box may sit from a signer's printed name (in
 * points, edge to edge) and still be taken as theirs. A signature sits right on
 * top of or beside the name; the next signer's block is further away.
 */
const MAX_NAME_TO_SIGNATURE_GAP = 60;

/**
 * Detected boxes from the LiteParse engine (`type` "liteparse:..."), which box
 * the ink itself. Apryse boxes can land on a typed date or an email, so a
 * signer is never snapped to one.
 */
const LITEPARSE_FIELD_PREFIX = 'liteparse:';

/** Points within which two name-to-box gaps count as the same. */
const GAP_TIE = 4;

interface SignerLocation {
  pageNumber: number | null;
  rect?: readonly number[] | null;
}

interface Candidate {
  centreDistance: number;
  field: DetectedSignatureField;
  gap: number;
  /** Share of the box's width lying across the name. */
  overlapShare: number;
}

interface RectEdges {
  bottom: number;
  left: number;
  right: number;
  top: number;
}

/**
 * Where each signer's card jumps to, in the order given. Two signers never
 * share a detected box: names and boxes on a page pair up nearest first, so
 * when both names sit under one row of signatures each takes the one over it.
 * A signer left without a box falls back as `getSignerSignatureTarget` says.
 */
export function getSignerSignatureTargets(
  signers: readonly SignerLocation[],
  fields: readonly DetectedSignatureField[]
): (DetectedSignatureField | null)[] {
  const pairs = signers.flatMap((signer, signerIndex) => {
    const name = signer.rect ? getRectEdges(signer.rect) : null;
    if (!name || !signer.pageNumber) return [];
    return getSnappableFields(fields, signer.pageNumber).flatMap(field => {
      const candidate = getCandidate(name, field);
      return candidate ? [{ ...candidate, signerIndex }] : [];
    });
  });

  const snapped = new Map<number, DetectedSignatureField>();
  const usedFields = new Set<DetectedSignatureField>();
  const ordered = pairs.sort((a, b) => compareCandidates(a, b) || a.signerIndex - b.signerIndex);
  for (const pair of ordered) {
    if (!snapped.has(pair.signerIndex) && !usedFields.has(pair.field)) {
      snapped.set(pair.signerIndex, pair.field);
      usedFields.add(pair.field);
    }
  }

  const freeFields = fields.filter(field => !usedFields.has(field));
  return signers.map(
    (signer, index) =>
      snapped.get(index) ??
      getSignerSignatureTarget(signer, {
        fields: freeFields,
        signersOnPage: signers.filter(other => other.pageNumber === signer.pageNumber).length,
      })
  );
}

/**
 * Where a signer's card jumps to:
 * - the LiteParse signature box nearest their printed name, when one sits
 *   right beside it, so the outline holds the ink rather than the name;
 * - otherwise around their printed name, widened to take in the signature;
 * - with no name position, a detected box only when it can only be theirs
 *   (the one box, and the one signer, on that page).
 * Otherwise nothing, so the card stays plain rather than pointing at the wrong
 * spot, which is what Apryse's boxes do when they land on a typed date or an
 * email.
 */
export function getSignerSignatureTarget(
  signer: SignerLocation,
  context: {
    fields: readonly DetectedSignatureField[];
    signersOnPage: number;
  }
): DetectedSignatureField | null {
  const { fields, signersOnPage } = context;
  const { pageNumber, rect } = signer;
  if (!pageNumber) return null;

  const pageFields = fields.filter(field => field.pageNumber === pageNumber);
  const name = rect ? getRectEdges(rect) : null;
  if (name) {
    const nearest = getNearestField(name, getSnappableFields(fields, pageNumber));
    if (nearest) return nearest;
    return {
      appearsSigned: true,
      confidence: 1,
      pageNumber,
      rect: [
        Math.max(0, name.left - NAME_PADDING.sides),
        Math.max(0, name.top - NAME_PADDING.above),
        name.right + NAME_PADDING.sides,
        name.bottom + NAME_PADDING.below,
      ],
      type: 'signerName',
    };
  }

  const [onlyField] = pageFields;
  return pageFields.length === 1 && signersOnPage === 1 && onlyField ? onlyField : null;
}

/** The page's signed LiteParse boxes, the only ones a signer is snapped to. */
function getSnappableFields(
  fields: readonly DetectedSignatureField[],
  pageNumber: number
): DetectedSignatureField[] {
  return fields.filter(
    field =>
      field.pageNumber === pageNumber &&
      field.type.startsWith(LITEPARSE_FIELD_PREFIX) &&
      field.appearsSigned
  );
}

/** The field nearest the name, ranked as `compareCandidates` says. */
function getNearestField(
  name: RectEdges,
  fields: readonly DetectedSignatureField[]
): DetectedSignatureField | null {
  let nearest: Candidate | null = null;
  for (const field of fields) {
    const candidate = getCandidate(name, field);
    if (candidate && (!nearest || compareCandidates(candidate, nearest) < 0)) nearest = candidate;
  }
  return nearest?.field ?? null;
}

/** A detected box near enough to a name to be its signature, with what ranks it. */
function getCandidate(name: RectEdges, field: DetectedSignatureField): Candidate | null {
  const box = getRectEdges(field.rect);
  const gap = getNameToFieldGap(name, field);
  if (!box || gap === null || gap > MAX_NAME_TO_SIGNATURE_GAP) return null;
  const across = Math.min(box.right, name.right) - Math.max(box.left, name.left);
  return {
    centreDistance: Math.abs((box.left + box.right) / 2 - (name.left + name.right) / 2),
    field,
    gap,
    overlapShare: Math.max(0, across) / Math.max(1, box.right - box.left),
  };
}

/**
 * Nearest box first. Boxes hug their ink, so two in one row rarely end at the
 * same height; gaps within `GAP_TIE` points tie, and then the box lying more
 * fully across the name wins, then the one centred on it.
 */
function compareCandidates(a: Candidate, b: Candidate): number {
  if (Math.abs(a.gap - b.gap) > GAP_TIE) return a.gap - b.gap;
  return b.overlapShare - a.overlapShare || a.centreDistance - b.centreDistance;
}

/** Edge-to-edge distance from a printed name to a detected box; null when the box has no rect. */
function getNameToFieldGap(name: RectEdges, field: DetectedSignatureField): number | null {
  const box = getRectEdges(field.rect);
  if (!box) return null;
  const horizontal = Math.max(0, box.left - name.right, name.left - box.right);
  const vertical = Math.max(0, box.top - name.bottom, name.top - box.bottom);
  return Math.hypot(horizontal, vertical);
}

/** The edges of an `[x1, y1, x2, y2]` rect whose corners may arrive in either order. */
function getRectEdges(rect: readonly number[]): RectEdges | null {
  const [x1, y1, x2, y2] = rect;
  if (x1 === undefined || y1 === undefined || x2 === undefined || y2 === undefined) return null;

  return {
    bottom: Math.max(y1, y2),
    left: Math.min(x1, x2),
    right: Math.max(x1, x2),
    top: Math.min(y1, y2),
  };
}
